import { apiService } from './api';

export interface ReviewStep {
  id: string;
  step_name: string;
  step_order: number;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  started_at: string | null;
  completed_at: string | null;
  progress_percentage: number;
  status_message: string;
  estimated_duration_seconds: number;
  result: any;
  error_message: string | null;
}

export interface Review {
  id: string;
  game_id: string;
  game_version_id: string | null;
  editor_edit_id: string;
  status: 'pending' | 'in_progress' | 'passed' | 'failed' | 'needs_manual_review' | 'published' | 'outdated';
  created_by: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  published_at: string | null;
  result: any;
  steps?: ReviewStep[];
  progress?: {
    completed: number;
    total: number;
    percentage: number;
    currentStep?: {
      name: string;
      message: string;
      progress: number;
    };
  };
}

export interface SubmitReviewResponse {
  success: boolean;
  review: Review;
  message: string;
}

export interface ReviewStatusResponse {
  success: boolean;
  review: Review;
}

class ReviewService {
  /**
   * Submit an editor version for review
   */
  async submitForReview(projectId: string, editorVersionId: string): Promise<SubmitReviewResponse> {
    const response = await apiService.post<{ success: boolean; data: Review; message?: string }>('/reviews', {
      gameId: projectId,
      editorVersionId
    });
    return {
      success: response.data.success,
      review: response.data.data,
      message: response.data.message || 'Review submitted successfully'
    };
  }

  /**
   * Get review status with detailed progress
   */
  async getReviewStatus(reviewId: string): Promise<ReviewStatusResponse> {
    const response = await apiService.get<{ success: boolean; data: Review }>(`/reviews/${reviewId}`);
    return {
      success: response.data.success,
      review: response.data.data
    };
  }

  /**
   * Get all reviews for a game
   */
  async getGameReviews(gameId: string): Promise<Review[]> {
    const response = await apiService.get<{ success: boolean; data: Review[] }>(
      `/reviews/game/${gameId}`
    );
    return response.data.data || [];
  }

  /**
   * Get active review for a game (if any)
   */
  async getActiveReview(gameId: string): Promise<Review | null> {
    const response = await apiService.get<{ success: boolean; data: Review | null }>(
      `/reviews/game/${gameId}/active`
    );
    return response.data.data;
  }

  /**
   * Poll review status until completion
   * Returns a promise that resolves when review is complete
   */
  async pollReviewStatus(
    reviewId: string,
    onProgress?: (review: Review) => void,
    intervalMs: number = 5000
  ): Promise<Review> {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const response = await this.getReviewStatus(reviewId);
          const review = response.review;

          // Call progress callback if provided
          if (onProgress) {
            onProgress(review);
          }

      // Check if review is complete
      if (['passed', 'failed', 'needs_manual_review', 'published', 'outdated'].includes(review.status)) {
        resolve(review);
        return;
      }

          // Continue polling
          setTimeout(poll, intervalMs);
        } catch (error) {
          reject(error);
        }
      };

      poll();
    });
  }
}

export const reviewService = new ReviewService();

